import { memo } from 'react';

import { useTrackDeletedContentCollectionLink } from '@air/analytics';
import { type Form, type ShortUrlResponse } from '@air/api/types';
import { Button } from '@air/primitive-button';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useDeleteShortId } from '~/swr-hooks/shortUrl/useDeleteShortId';
import { type EditShareLinkView } from '~/types/ShareLink';

type ContentCollectionDeleteActionsProps = {
  form: Form;
  shortIdInfo: ShortUrlResponse;
  setView: (view: EditShareLinkView) => void;
};

export const ContentCollectionDeleteActions = memo(
  ({ form, shortIdInfo, setView }: ContentCollectionDeleteActionsProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { trackDeletedContentCollectionLink } = useTrackDeletedContentCollectionLink();
    const { deleteShortId } = useDeleteShortId();

    return (
      <div className="mt-8 flex justify-end gap-3">
        <Button
          appearance="ghost"
          color="grey"
          data-testid="CONTENT_COLLECTION_DELETE_ACTIONS_CANCEL"
          onClick={() => setView('edit')}
          size="large"
        >
          Cancel
        </Button>
        <Button
          appearance="filled"
          color="red"
          data-testid="CONTENT_COLLECTION_DELETE_ACTIONS_CONFIRM"
          onClick={() => {
            const workspaceId = currentWorkspace?.id;
            if (!workspaceId) {
              throw new Error('No workspace id');
            }
            deleteShortId.mutate(
              {
                workspaceId,
                shortId: shortIdInfo.id,
                boardContext: undefined,
                id: form.id,
              },
              {
                onSuccess: () => {
                  trackDeletedContentCollectionLink();
                  setView('create');
                },
              },
            );
          }}
          isLoading={deleteShortId.isPending}
          size="large"
        >
          Delete link
        </Button>
      </div>
    );
  },
);

ContentCollectionDeleteActions.displayName = 'ContentCollectionDeleteActions';
